import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePagination } from "@/hooks/usePagination";
import { usePermissions } from "@/hooks/usePermissions";
import { PersonCard } from "@/components/people/PersonCard";
import { PersonDetails } from "@/components/people/PersonDetails";
import { ImageUpload } from "@/components/ui/image-upload";
import { Search, Plus, ChevronLeft, ChevronRight, User, Lock } from "lucide-react";

type Person = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  status: string | null;
  tags: string[] | null;
  is_decision_maker: boolean | null;
  notes: string | null;
  linkedin_url: string | null;
  current_client_id: string | null;
  avatar_url?: string | null;
  created_at: string;
  updated_at: string;
  clients?: {
    name: string;
  };
};

type Client = {
  id: string;
  name: string;
};

const formatDateDistance = (date: string) => {
  const now = new Date();
  const activityDate = new Date(date);
  const diffInDays = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

export default function People() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientIdFromUrl = searchParams.get('client_id');
  
  const { canView, canEdit, canCreate, canDelete, userRole } = usePermissions();
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedTags, setSelectedTags] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // New person form state
  const [newPerson, setNewPerson] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    currentClient: "",
    status: "new",
    isDecisionMaker: false,
    notes: "",
    tags: "",
    avatarUrl: ""
  });

  // Handle URL parameters for pre-filling add contact form
  useEffect(() => {
    const addContact = searchParams.get('addContact');
    const email = searchParams.get('email');
    const firstName = searchParams.get('firstName');
    const clientName = searchParams.get('clientName');

    if (addContact === 'true') {
      // Pre-fill the form
      setNewPerson(prev => ({
        ...prev,
        email: email || "",
        firstName: firstName || email?.split("@")[0] || "",
        currentClient: clientName || "",
      }));
      
      // Open the modal
      setIsAddModalOpen(true);
      
      // Clear the URL parameters
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('addContact');
      newParams.delete('email');
      newParams.delete('firstName');
      newParams.delete('clientName');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch people with client information
  const { data: people = [], isLoading: peopleLoading } = useQuery({
    queryKey: ["people"],
    queryFn: async () => {
      // Use secure function to get people data
      const { data: peopleData, error: peopleError } = await supabase
        .rpc("get_people_secure");

      if (peopleError) {
        console.error("Error fetching people:", peopleError);
        throw peopleError;
      }

      // Get client data for people who have clients
      const clientIds = [...new Set(peopleData?.filter(p => p.current_client_id).map(p => p.current_client_id))];
      let clientsData = [];
      
      if (clientIds.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from("clients")
          .select("id, name")
          .in("id", clientIds);
          
        if (clientsError) {
          console.error("Error fetching clients:", clientsError);
          throw clientsError;
        }
        clientsData = clients || [];
      }

      // Map people with client data
      const enrichedPeople = peopleData?.map(person => ({
        ...person,
        clients: person.current_client_id 
          ? { name: clientsData.find(c => c.id === person.current_client_id)?.name || '' }
          : null
      })) || [];

      return enrichedPeople as Person[];
    }
  });

  // Fetch clients for dropdown
  const { data: clients = [] } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data as Client[];
    }
  });

  // Add person mutation
  const addPersonMutation = useMutation({
    mutationFn: async (personData: typeof newPerson) => {
      const clientId = personData.currentClient ? 
        clients.find(c => c.name === personData.currentClient)?.id : null;

      const { data: personId, error } = await supabase
        .rpc("insert_person_secure", {
          person_data: {
            first_name: personData.firstName,
            last_name: personData.lastName,
            email: personData.email || null,
            phone: personData.phone || null,
            position: personData.position || null,
            current_client_id: clientId,
            status: personData.status,
            is_decision_maker: personData.isDecisionMaker,
            notes: personData.notes || null,
            tags: personData.tags ? personData.tags.split(",").map(t => t.trim()) : null,
            avatar_url: personData.avatarUrl || null
          }
        });

      if (error) {
        throw new Error(error.message || "Failed to create person");
      }

      if (!personId) {
        throw new Error("Failed to create person");
      }

      return personId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["people"] });
      setIsAddModalOpen(false);
      toast({
        title: "Success",
        description: "Person added successfully",
      });
      
      // Check if this person was created from the email matcher callback
      const callbackData = sessionStorage.getItem('emailMatcherCallback');
      if (callbackData) {
        try {
          const { activityId, email } = JSON.parse(callbackData);
          // Clear the callback data
          sessionStorage.removeItem('emailMatcherCallback');
          
          // Trigger activities refresh to auto-link the new person
          queryClient.invalidateQueries({ queryKey: ["activities"] });
          
          toast({
            title: "Contact linked",
            description: "The new contact will be automatically linked to the email activity",
          });
        } catch (error) {
          console.error('Error processing email matcher callback:', error);
        }
      }
      
      // If we came from a client page, navigate back to it
      if (clientIdFromUrl) {
        navigate(`/clients?selectedClientId=${clientIdFromUrl}`);
        return;
      }
      
      // Reset form
      setNewPerson({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        position: "",
        currentClient: "",
        status: "new",
        isDecisionMaker: false,
        notes: "",
        tags: "",
        avatarUrl: ""
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to add person: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Edit person mutation
  const editPersonMutation = useMutation({
    mutationFn: async (personData: { person: Person; updates: typeof newPerson }) => {
      const clientId = personData.updates.currentClient ? 
        clients.find(c => c.name === personData.updates.currentClient)?.id : null;

      const { data: result, error } = await supabase
        .rpc("update_person_secure", {
          person_id: personData.person.id,
          person_data: {
            first_name: personData.updates.firstName,
            last_name: personData.updates.lastName,
            email: personData.updates.email || null,
            phone: personData.updates.phone || null,
            position: personData.updates.position || null,
            current_client_id: clientId,
            status: personData.updates.status,
            is_decision_maker: personData.updates.isDecisionMaker,
            notes: personData.updates.notes || null,
            tags: personData.updates.tags ? personData.updates.tags.split(",").map(t => t.trim()) : null,
            avatar_url: personData.updates.avatarUrl || null
          }
        });

      if (error) {
        throw new Error(error.message || "Failed to update person");
      }

      if (!result) {
        throw new Error("Failed to update person");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["people"] });
      setIsEditModalOpen(false);
      setEditingPerson(null);
      toast({
        title: "Success",
        description: "Person updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update person: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Delete person mutation
  const deletePersonMutation = useMutation({
    mutationFn: async (personId: string) => {
      // First delete related employment history
      await supabase
        .from("employment_history")
        .delete()
        .eq("person_id", personId);

      // Delete related activities
      await supabase
        .from("activities")
        .delete()
        .eq("person_id", personId);

      // Delete related proposal recipients
      await supabase
        .from("proposal_recipients")
        .delete()
        .eq("person_id", personId);

      // Finally delete the person
      const { data: result, error } = await supabase
        .rpc("delete_person_secure", {
          person_id: personId
        });

      if (error) {
        throw new Error(error.message || "Failed to delete person");
      }

      if (!result) {
        throw new Error("Failed to delete person");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["people"] });
      setSelectedPerson(null);
      toast({
        title: "Success",
        description: "Person deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete person: " + error.message,
        variant: "destructive",
      });
    }
  });

  // Filter people based on search and filters
  const filteredPeople = people.filter(person => {
    const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
    const clientName = person.clients?.name || "";
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         (person.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (person.notes || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                         clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = !selectedCompany || selectedCompany === "all" || clientName === selectedCompany;
    const matchesStatus = !selectedStatus || selectedStatus === "all" || person.status === selectedStatus;
    const matchesRole = !selectedRole || selectedRole === "all" || 
                       (person.position || "").toLowerCase().includes(selectedRole.toLowerCase());
    const matchesTags = !selectedTags || selectedTags === "all" || 
                       (person.tags || []).some(tag => tag.includes(selectedTags));
    
    return matchesSearch && matchesCompany && matchesStatus && matchesRole && matchesTags;
  });

  // Use pagination for filtered people
  const {
    items: paginatedPeople,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    resetPagination
  } = usePagination(filteredPeople, 20);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [searchTerm, selectedCompany, selectedStatus, selectedRole, selectedTags, resetPagination]);

  // Set first person as selected when people load
  useEffect(() => {
    if (paginatedPeople.length > 0 && !selectedPerson) {
      setSelectedPerson(paginatedPeople[0]);
    }
  }, [paginatedPeople, selectedPerson]);

  // Set default client from URL when component loads
  useEffect(() => {
    if (clientIdFromUrl && clients.length > 0) {
      const selectedClient = clients.find(c => c.id === clientIdFromUrl);
      if (selectedClient) {
        setNewPerson(prev => ({
          ...prev,
          currentClient: selectedClient.name
        }));
        // Auto-open the modal when coming from client page
        setIsAddModalOpen(true);
      }
    }
  }, [clientIdFromUrl, clients]);

  const handleAddPerson = () => {
    addPersonMutation.mutate(newPerson);
  };

  const handleEditPerson = (person: Person) => {
    setEditingPerson(person);
    // Pre-fill the form with current person data
    setNewPerson({
      firstName: person.first_name,
      lastName: person.last_name,
      email: person.email || "",
      phone: person.phone || "",
      position: person.position || "",
      currentClient: person.clients?.name || "",
      status: person.status || "active",
      isDecisionMaker: person.is_decision_maker || false,
      notes: person.notes || "",
      tags: (person.tags || []).join(", "),
      avatarUrl: person.avatar_url || ""
    });
    setIsEditModalOpen(true);
  };

  const handleUpdatePerson = () => {
    if (editingPerson) {
      editPersonMutation.mutate({ person: editingPerson, updates: newPerson });
    }
  };

  // Business insights - removed as requested
  // const inactivePeople = people.filter(person => person.status === "inactive").length;
  // const recentlyMoved = people.filter(person => (person.tags || []).includes("moved-company")).length;
  // const decisionMakers = people.filter(person => person.is_decision_maker).length;

  // Get last activity for a person
  const getLastActivity = (personId: string) => {
    // This would need to be calculated from activities, for now return placeholder
    return formatDateDistance(new Date().toISOString());
  };

  // Check permissions and error states
  const canViewPeople = canView('people');
  const canManagePeople = canEdit('people');
  const canCreatePeople = canCreate('people');
  const canDeletePeople = canDelete('people');

  // Show error feedback for failed delete operation
  useEffect(() => {
    if (deletePersonMutation.error) {
      toast({
        title: "Error",
        description: "Something went wrong while deleting the person.",
        variant: "destructive",
      });
    }
  }, [deletePersonMutation.error, toast]);

  if (peopleLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading people...</p>
        </div>
      </div>
    );
  }

  // Check if user has permission to view people
  if (!canViewPeople) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Lock className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-semibold mb-2">Access Denied</p>
          <p className="text-muted-foreground">You don't have permission to view people.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {/* Left sidebar - People list */}
      <div className="w-1/3 border-r border-border p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">People</h1>
          {canCreatePeople ? (
            <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  New
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Person</DialogTitle>
                </DialogHeader>
                 <div className="space-y-4">
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <Label htmlFor="firstName">First Name</Label>
                       <Input 
                         value={newPerson.firstName}
                         onChange={(e) => setNewPerson({...newPerson, firstName: e.target.value})}
                         placeholder="First name"
                       />
                     </div>
                     <div>
                       <Label htmlFor="lastName">Last Name</Label>
                       <Input 
                         value={newPerson.lastName}
                         onChange={(e) => setNewPerson({...newPerson, lastName: e.target.value})}
                         placeholder="Last name"
                       />
                     </div>
                   </div>

                   <ImageUpload
                     value={newPerson.avatarUrl}
                     onChange={(url) => setNewPerson({...newPerson, avatarUrl: url})}
                     bucket="people-avatars"
                     variant="circle"
                     size="md"
                     label="Avatar"
                   />
                   
                   <div>
                     <Label htmlFor="email">Email</Label>
                     <Input 
                       type="email"
                       value={newPerson.email}
                       onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
                       placeholder="email@company.com"
                     />
                   </div>

                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      value={newPerson.phone}
                      onChange={(e) => setNewPerson({...newPerson, phone: e.target.value})}
                      placeholder="+30 210 123 4567"
                    />
                  </div>

                  <div>
                    <Label htmlFor="position">Position</Label>
                    <Input 
                      value={newPerson.position}
                      onChange={(e) => setNewPerson({...newPerson, position: e.target.value})}
                      placeholder="Job title"
                    />
                  </div>

                  <div>
                    <Label htmlFor="currentClient">Current Company</Label>
                    <Select value={newPerson.currentClient} onValueChange={(value) => setNewPerson({...newPerson, currentClient: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.name}>{client.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={newPerson.isDecisionMaker}
                      onCheckedChange={(checked) => setNewPerson({...newPerson, isDecisionMaker: checked})}
                    />
                    <Label>Decision Maker</Label>
                  </div>

                  <div>
                    <Label htmlFor="tags">Tags (comma separated)</Label>
                    <Input 
                      value={newPerson.tags}
                      onChange={(e) => setNewPerson({...newPerson, tags: e.target.value})}
                      placeholder="decision-maker, key-contact"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea 
                      value={newPerson.notes}
                      onChange={(e) => setNewPerson({...newPerson, notes: e.target.value})}
                      placeholder="Additional notes"
                      rows={3}
                    />
                  </div>

                  <Button 
                    onClick={handleAddPerson} 
                    className="w-full"
                    disabled={addPersonMutation.isPending}
                  >
                    {addPersonMutation.isPending ? "Adding..." : "Add Person"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <div className="text-sm text-muted-foreground">
              Role: {userRole} (View only)
            </div>
          )}
        </div>


        {/* Filters */}
        <div className="space-y-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search people..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.name}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="new">New</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="head">Head</SelectItem>
                <SelectItem value="analyst">Analyst</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedTags} onValueChange={setSelectedTags}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                <SelectItem value="decision-maker">Decision Maker</SelectItem>
                <SelectItem value="key-contact">Key Contact</SelectItem>
                <SelectItem value="budget-holder">Budget Holder</SelectItem>
                <SelectItem value="moved-company">Moved Company</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* People list with pagination */}
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {paginatedPeople.map((person) => (
            <PersonCard
              key={person.id}
              person={person}
              isSelected={selectedPerson?.id === person.id}
              onClick={() => setSelectedPerson(person)}
              getLastActivity={getLastActivity}
            />
          ))}
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={!hasPrevPage}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={!hasNextPage}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* Right content - Person details */}
      <div className="flex-1 p-6">
        {selectedPerson ? (
          <PersonDetails
            person={selectedPerson}
            onEdit={canManagePeople ? handleEditPerson : undefined}
            onDelete={canDeletePeople ? (personId) => deletePersonMutation.mutate(personId) : undefined}
            isDeleting={deletePersonMutation.isPending}
            getLastActivity={getLastActivity}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-muted-foreground">
              <User className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p>Select a person to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Edit Person Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Person</DialogTitle>
          </DialogHeader>
           <div className="space-y-4">
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label htmlFor="firstName">First Name</Label>
                 <Input 
                   value={newPerson.firstName}
                   onChange={(e) => setNewPerson({...newPerson, firstName: e.target.value})}
                   placeholder="First name"
                 />
               </div>
               <div>
                 <Label htmlFor="lastName">Last Name</Label>
                 <Input 
                   value={newPerson.lastName}
                   onChange={(e) => setNewPerson({...newPerson, lastName: e.target.value})}
                   placeholder="Last name"
                 />
               </div>
             </div>

             <ImageUpload
               value={newPerson.avatarUrl}
               onChange={(url) => setNewPerson({...newPerson, avatarUrl: url})}
               bucket="people-avatars"
               variant="circle"
               size="md"
               label="Avatar"
             />
             
             <div>
               <Label htmlFor="email">Email</Label>
               <Input 
                 type="email"
                 value={newPerson.email}
                 onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
                 placeholder="email@company.com"
               />
             </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input 
                value={newPerson.phone}
                onChange={(e) => setNewPerson({...newPerson, phone: e.target.value})}
                placeholder="+30 210 123 4567"
              />
            </div>

            <div>
              <Label htmlFor="position">Position</Label>
              <Input 
                value={newPerson.position}
                onChange={(e) => setNewPerson({...newPerson, position: e.target.value})}
                placeholder="Job title"
              />
            </div>

            <div>
              <Label htmlFor="currentClient">Current Company</Label>
              <Select value={newPerson.currentClient} onValueChange={(value) => setNewPerson({...newPerson, currentClient: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.name}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch 
                checked={newPerson.isDecisionMaker}
                onCheckedChange={(checked) => setNewPerson({...newPerson, isDecisionMaker: checked})}
              />
              <Label>Decision Maker</Label>
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input 
                value={newPerson.tags}
                onChange={(e) => setNewPerson({...newPerson, tags: e.target.value})}
                placeholder="decision-maker, key-contact"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea 
                value={newPerson.notes}
                onChange={(e) => setNewPerson({...newPerson, notes: e.target.value})}
                placeholder="Additional notes"
                rows={3}
              />
            </div>

            <Button 
              onClick={handleUpdatePerson} 
              className="w-full"
              disabled={editPersonMutation.isPending}
            >
              {editPersonMutation.isPending ? "Updating..." : "Update Person"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}